import React, {useCallback} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {filterMessages} from 'components/Filters/filterMessages'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {usePriceFilter} from 'hooks/usePriceFilter'
import {getCurrencyCode} from 'modules/meta/selectors'
import {searchParamToFacet} from 'modules/search/constants'
import {getListOfValues} from 'modules/search/selectors'
import {FilterUrlParams} from 'modules/search/types'
import {prop} from 'ramda'
import {PROPERTY_TYPE_HOTEL_FACET_IDS} from 'utils/facilities'
import {getFacetLabel} from 'utils/filters'

import {Icon} from '@vio/atlas/Icon'
import {Pill} from '@vio/atlas/Pill'
import {
  getReviewSentimentKey,
  reviewSentimentMessages
} from '@vio/core/src/hotel/reviewSentiments'
import {getEnabledCurrencies} from '@vio/core/src/localization/business/currency'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

type Props = Pick<
  ReturnType<typeof useFiltersForm>,
  'getFilters' | 'toggleMultiFilter' | 'toggleFilters'
>

interface PillProps extends Props {
  isActive: boolean
  className?: string
}

export const FacilityPills: React.FC<PillProps> = ({
  getFilters,
  toggleMultiFilter,
  isActive,
  className
}) => {
  const listOfValues = useSelector(getListOfValues)

  const {facilities} = getFilters()
  const getLabel = getFacetLabel('Facility', listOfValues)
  const facilitiesKey = searchParamToFacet.facilities.param

  if (facilities) {
    return (
      <>
        {facilities.map(facetId => {
          return (
            <Pill
              className={className}
              key={`Facility:${facetId}`}
              isSelected={isActive}
              iconEnd={<Icon name={'Close'} />}
              onClick={() =>
                toggleMultiFilter(
                  facilitiesKey as keyof FilterUrlParams,
                  facetId
                )
              }
            >
              {getLabel(facetId)}
            </Pill>
          )
        })}
      </>
    )
  }

  return null
}

export const ThemePills: React.FC<PillProps> = ({
  getFilters,
  toggleMultiFilter,
  isActive,
  className
}) => {
  const {themes} = getFilters()
  const listOfValues = useSelector(getListOfValues)
  const getLabel = getFacetLabel('Theme', listOfValues)
  const themesKey = searchParamToFacet.themes.param

  if (themes) {
    return (
      <>
        {themes.map(themeId => {
          return (
            <Pill
              className={className}
              key={`Theme:${themeId}`}
              isSelected={isActive}
              iconEnd={<Icon name={'Close'} />}
              onClick={() =>
                toggleMultiFilter(themesKey as keyof FilterUrlParams, themeId)
              }
            >
              {getLabel(themeId)}
            </Pill>
          )
        })}
      </>
    )
  }

  return null
}

export const PropertyTypePills: React.FC<PillProps> = ({
  getFilters,
  toggleMultiFilter,
  isActive,
  className
}) => {
  const {propertyTypes} = getFilters()
  const listOfValues = useSelector(getListOfValues)
  const getLabel = getFacetLabel('PropertyType', listOfValues)
  const propertyTypesKey = searchParamToFacet.propertyTypes.param
  const propertyTypesList = propertyTypes ? [propertyTypes].flat() : []

  const getIconStart = propertyId =>
    PROPERTY_TYPE_HOTEL_FACET_IDS.has(propertyId) ? 'Building' : 'House'

  // hotels property types should be displayed first
  const propertyTypesListSorted = [...propertyTypesList].sort(a =>
    PROPERTY_TYPE_HOTEL_FACET_IDS.has(a) ? -1 : 0
  )

  if (propertyTypesListSorted.length) {
    return (
      <>
        {propertyTypesListSorted.map(propertyId => {
          return (
            <Pill
              className={className}
              key={`PropertyType:${propertyId}`}
              isSelected={isActive}
              iconStart={<Icon name={getIconStart(propertyId)} />}
              iconEnd={<Icon name={'Close'} />}
              onClick={() =>
                toggleMultiFilter(
                  propertyTypesKey as keyof FilterUrlParams,
                  propertyId
                )
              }
            >
              {getLabel(propertyId)}
            </Pill>
          )
        })}
      </>
    )
  }

  return null
}

export const StarRatingsPill: React.FC<PillProps> = ({
  getFilters,
  toggleMultiFilter,
  isActive,
  className
}) => {
  const {starRatings} = getFilters()
  const starRatingKey = searchParamToFacet.starRatings.param

  if (starRatings) {
    return (
      <>
        {starRatings.map(starRating => {
          return (
            <Pill
              className={className}
              key={`StarRatings:${starRating}`}
              isSelected={isActive}
              iconStart={<Icon name={'Star'} />}
              iconEnd={<Icon name={'Close'} />}
              onClick={() =>
                toggleMultiFilter(
                  starRatingKey as keyof FilterUrlParams,
                  starRating
                )
              }
            >
              <FormattedMessageWrapper
                id="filters.starRating"
                defaultMessage="{starRating, plural, one {1‑star} other {#‑star}}"
                values={{starRating}}
              />
            </Pill>
          )
        })}
      </>
    )
  }

  return null
}

export const FreeCancellationPill: React.FC<PillProps> = ({
  getFilters,
  toggleFilters,
  isActive,
  className
}) => {
  const {formatMessage} = useIntl()

  const filters = getFilters()
  const {freeCancellation} = filters

  const handleSingleFilterChange = useCallback(
    (value: FilterUrlParams) => {
      toggleFilters({
        ...filters,
        ...value
      })
    },
    [toggleFilters, filters]
  )

  return freeCancellation ? (
    <Pill
      className={className}
      isSelected={isActive}
      iconEnd={<Icon name={'Close'} />}
      onClick={() => handleSingleFilterChange({freeCancellation: undefined})}
    >
      {formatMessage(filterMessages.freeCancellationTitle)}
    </Pill>
  ) : null
}

export const ReviewScorePill: React.FC<PillProps> = ({
  getFilters,
  toggleFilters,
  isActive,
  className
}) => {
  const {formatMessage} = useIntl()
  const filters = getFilters()
  const {reviewScore} = filters
  const handleSingleFilterChange = useCallback(
    (value: FilterUrlParams) => {
      toggleFilters({
        ...filters,
        ...value
      })
    },
    [toggleFilters, filters]
  )
  if (reviewScore) {
    const sentimentMessageKey = getReviewSentimentKey(Number(reviewScore))
    const translatedSentiment = sentimentMessageKey
      ? reviewSentimentMessages[sentimentMessageKey]
      : null

    return (
      <Pill
        className={className}
        isSelected={isActive}
        iconEnd={<Icon name={'Close'} />}
        onClick={() => handleSingleFilterChange({reviewScore: undefined})}
      >
        {`${formatMessage(translatedSentiment)} ${reviewScore}+`}
      </Pill>
    )
  }

  return null
}

export const PricePill: React.FC<PillProps> = ({
  getFilters,
  toggleFilters,
  isActive,
  className
}) => {
  const currencyCode = useSelector(getCurrencyCode)
  const {getTotalPriceFilter} = usePriceFilter()

  const filters = getFilters()
  const priceMin = getTotalPriceFilter(filters.priceMin)
  const priceMax = getTotalPriceFilter(filters.priceMax)

  const handleSingleFilterChange = useCallback(
    (value: FilterUrlParams) => {
      toggleFilters({
        ...filters,
        ...value
      })
    },
    [toggleFilters, filters]
  )
  if (priceMin || priceMax) {
    const enabledCurrencies = getEnabledCurrencies(
      process.env.NEXT_PUBLIC_ENABLED_CURRENCIES
    )
    const currency = enabledCurrencies.find(
      currency => currency.code === currencyCode
    )
    const currencySymbol = prop('symbol', currency)

    return (
      <Pill
        className={className}
        isSelected={isActive}
        iconEnd={<Icon name={'Close'} />}
        onClick={() =>
          handleSingleFilterChange({
            priceMin: undefined,
            priceMax: undefined
          })
        }
      >
        {currencySymbol}
        {priceMin} - {currencySymbol}
        {priceMax}
      </Pill>
    )
  }

  return null
}

export const MealPills: React.FC<PillProps> = ({
  getFilters,
  toggleMultiFilter,
  isActive,
  className
}) => {
  const {formatMessage} = useIntl()
  const {amenities} = getFilters()

  if (!amenities || amenities.length === 0) return null

  return (
    <>
      {amenities.map(mealId => {
        return (
          <Pill
            className={className}
            key={`Amenities:${mealId}`}
            isSelected={isActive}
            iconEnd={<Icon name={'Close'} />}
            onClick={() => toggleMultiFilter('amenities', mealId)}
          >
            {formatMessage(filterMessages[mealId])}
          </Pill>
        )
      })}
    </>
  )
}
